import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

const showDelayMs = 150
const minVisibleMs = 400

/**
 * Tracks whether any query or mutation is in flight anywhere in the app
 * (via the shared `queryClient` context — no query key filters, so it
 * covers every domain with zero per-page wiring) and debounces the result
 * so a full-page loading overlay doesn't flicker on fast requests:
 * - waits `showDelayMs` of continuous in-flight activity before reporting `true`
 * - once shown, stays `true` for at least `minVisibleMs` after activity stops
 */
export function useGlobalFetchingState(): boolean {
  const fetchingCount = useIsFetching()
  const mutatingCount = useIsMutating()
  const isActive = fetchingCount + mutatingCount > 0

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isActive) {
      const showTimer = setTimeout(() => {
        setIsVisible(true)
      }, showDelayMs)

      return () => {
        clearTimeout(showTimer)
      }
    }

    const hideTimer = setTimeout(() => {
      setIsVisible(false)
    }, minVisibleMs)

    return () => {
      clearTimeout(hideTimer)
    }
  }, [isActive])

  return isVisible
}
